import { useEffect, useState } from "react";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import { PageHeader } from "@/components/layouts/page-header/index.tsx";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { useInvoices } from "@/app/vendors/invoices/invoices.hooks";
import { getInvoiceSubject } from "@/lib/invoices/invoice-subject";
import { toInvoiceListDateFilters, type DateRange } from "@/lib/invoices/invoice-search";
import type { InvoiceListFilters, InvoiceListJoin } from "@/repositories/invoice-records";
import { useDebounce } from "@/hooks/useDebounce";
import { CustomerInvoiceStatusBadge } from "./customer-invoice-status-badge";
import { formatInvoiceDate } from "./format-invoice-date";

const columns: ColumnDef<InvoiceListJoin>[] = [
  {
    header: "Invoice #",
    accessor: (row) => (
      <Link to={`/invoices/${row.id}`} className="text-primary text-sm font-semibold">
        #{row.invoice_number}
      </Link>
    ),
    sortable: true,
    sortKey: "invoice_number",
  },
  {
    header: "Vendor",
    accessor: (row) => <span className="text-sm">{row.vendor_name}</span>,
  },
  {
    header: "Billed For",
    accessor: (row) => {
      const subject = getInvoiceSubject(row);
      return (
        <div>
          <div className="text-sm">{subject.propertyName || <span className="text-muted-foreground">N/A</span>}</div>
          {subject.unitNumber && <div className="text-xs text-muted-foreground">Unit: {subject.unitNumber}</div>}
          {subject.periodRange && <div className="text-xs text-muted-foreground">Contract period: {subject.periodRange}</div>}
        </div>
      );
    },
  },
  {
    header: "Issue Date",
    accessor: (row) => formatInvoiceDate(row.issue_date),
    sortable: true,
    sortKey: "issue_date",
  },
  {
    header: "Due Date",
    accessor: (row) => formatInvoiceDate(row.due_date),
    sortable: true,
    sortKey: "due_date",
  },
  {
    header: "Amount",
    accessor: (row) => <span className="font-semibold">${row.amount.toFixed(2)}</span>,
    className: "text-right",
    cellClassName: "text-right",
  },
  {
    header: "Status",
    accessor: (row) => <CustomerInvoiceStatusBadge invoice={row} />,
  },
];

export default function CustomerInvoicesPage() {
  const [search, setSearch] = useState("");
  const [issueRange, setIssueRange] = useState<DateRange>({});
  const [dueRange, setDueRange] = useState<DateRange>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState("invoice_number");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [pageSize, debouncedSearch, issueRange, dueRange, sortKey, sortOrder]);

  const filters: InvoiceListFilters = { search: debouncedSearch, ...toInvoiceListDateFilters(issueRange, dueRange) };
  const { invoices, loading, total, error } = useInvoices(page, pageSize, filters, sortKey, sortOrder);

  const filterItems: FilterItem[] = [
    { type: "search", title: "Search by property, unit, or invoice number...", value: search, onValueChange: setSearch, grid: 4 },
    { type: "dateRange", title: "Issue Date", value: issueRange, onValueChange: setIssueRange, grid: 4 },
    { type: "dateRange", title: "Due Date", value: dueRange, onValueChange: setDueRange, grid: 4 },
  ];

  const changeSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(key);
    setSortOrder("asc");
  };

  const reset = () => {
    setSearch("");
    setIssueRange({});
    setDueRange({});
    setSortKey("invoice_number");
    setSortOrder("desc");
  };

  return (
    <MainLayout rightSidebar={false} breadcrumb={[{ name: "Invoices", href: "#" }]}>
      <PageHeader title="Invoices" subtitle="Invoices your vendors have billed you" />
      {error ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-destructive mb-2">Error loading invoices</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      ) : (
        <SimpleTable<InvoiceListJoin>
          columns={columns}
          data={invoices}
          loading={loading}
          totalCount={total}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          sortKey={sortKey}
          sortOrder={sortOrder}
          onSortChange={changeSort}
          filters={filterItems}
          onReset={reset}
          container={false}
          getRowId={(row) => row.id}
        />
      )}
    </MainLayout>
  );
}
